import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { LangContext, useLang } from '../../contexts/LangContext.js';
import {
  LineChart, BarChart, AreaChart, RadarChart, ScatterChart,
  Line, Bar, Area, Radar, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell, ComposedChart,
} from 'recharts';
import { COLORS_DARK, COLORS_LIGHT } from './theme.js';
import { useTheme } from '../../contexts/ThemeContext.js';
import { NAV_CATEGORIES, AF_CATS } from '../../constants/navCategories.js'; // [NAV-Dedup]

// COLORS: LangContext/ThemeContext 없이도 동작하는 런타임 참조
// V49 메인의 isDark 상태에 따라 동적으로 결정되나,
// 탭 파일은 일단 COLORS_LIGHT를 기본값으로 사용
// (isDark props가 필요하면 추후 ThemeContext로 교체)

// LangContext — V49 메인의 Provider 안에서 렌더되므로 동작함

// ============================================================
// advanced_widgets.js
// EV Powertrain Simulator — Phase 4 컴포넌트 분리
// 고급 분석 탭 전용 위젯 (3D 토크맵, AWD 지면반력, 섹션헤더)
// ============================================================

// ── AWDGroundForceViz ──
// [NAV-Dedup] NAV_CATEGORIES: constants/navCategories.js에서 상단 import로 처리됨



function AWDGroundForceViz({ params }) {
  const COLORS = useTheme();
  const lang   = useLang();
  const e      = (ko, en) => lang === 'en' ? en : ko;
  const [vizV,  setVizV]  = React.useState(80);    // km/h
  const [vizTq, setVizTq] = React.useState(50);    // torq %
  const [vizAy, setVizAy] = React.useState(0.0);   // lateral g

  const grf = React.useMemo(
    () => calcAWDGroundForceViz(params, vizV, vizTq, vizAy),
    [params, vizV, vizTq, vizAy]
  );

  // 마찰원 활용률 → 색상 (green→yellow→red)
  const utilColor = (u) => {
    const r = Math.round(Math.min(255, u * 2 * 255));
    const g = Math.round(Math.min(255, (1 - Math.max(0, u - 0.5) * 2) * 220));
    return `rgb(${r},${g},40)`;
  };
  // Fz 정규화 (최대값 대비 bar 길이)
  const fzMax = Math.max(1, grf.Fz_FL, grf.Fz_FR, grf.Fz_RL, grf.Fz_RR);
  const fzH   = (fz) => Math.max(4, Math.round((fz / fzMax) * 36));
  // 토크 화살표 길이 정규화
  const tMax  = Math.max(1, Math.abs(grf.T_FL), Math.abs(grf.T_FR), Math.abs(grf.T_RL), Math.abs(grf.T_RR));
  const tArrow = (t) => Math.max(4, Math.round(Math.abs(t) / tMax * 28));

  // SVG 좌표 상수 (탑뷰)
  const CX = 130, CY = 190;               // 차체 중심
  const BW = 52,  BH = 130;              // 차체 폭/길이
  const TW = 18,  TH = 32;              // 타이어 폭/길이
  const offs = { x: 46, y: 72 };        // 타이어 오프셋 (중심에서)

  const wheels = [
    { key:'FL', label:e('전좌','FL'), cx: CX-offs.x, cy: CY-offs.y, T: grf.T_FL, Fz: grf.Fz_FL, u: grf.frUtil.FL },
    { key:'FR', label:e('전우','FR'), cx: CX+offs.x, cy: CY-offs.y, T: grf.T_FR, Fz: grf.Fz_FR, u: grf.frUtil.FR },
    { key:'RL', label:e('후좌','RL'), cx: CX-offs.x, cy: CY+offs.y, T: grf.T_RL, Fz: grf.Fz_RL, u: grf.frUtil.RL },
    { key:'RR', label:e('후우','RR'), cx: CX+offs.x, cy: CY+offs.y, T: grf.T_RR, Fz: grf.Fz_RR, u: grf.frUtil.RR },
  ];

  const sliderStyle = { width:'100%', accentColor:'#a855f7', cursor:'pointer' };
  const kpiBox = (lbl, val, col) => (
    <div key={lbl} style={{ flex:1, background:COLORS.cardBg, borderRadius:7, padding:'8px 10px',
      border:`1px solid ${col}40`, textAlign:'center', minWidth:70 }}>
      <div style={{ fontSize:9, color:COLORS.textDim, marginBottom:2 }}>{lbl}</div>
      <div style={{ fontSize:14, fontWeight:900, color:col, fontFamily:'monospace' }}>{val}</div>
    </div>
  );

  return (
    <div style={{ marginTop:18, padding:'14px 16px', background:COLORS.cardBg,
      border:'1px solid #a855f730', borderRadius:10 }}>
      {/* ── 헤더 ── */}
      <div style={{ fontSize:12, fontWeight:800, color:'#a855f7', marginBottom:12 }}>
        🛞 {e('4륜 지면반력도 — 실시간 토크 벡터링 시각화','4-Wheel Ground Reaction — Real-time Torque Vectoring')}
        <span style={{ marginLeft:8, fontSize:9, fontWeight:500, color:COLORS.textDim }}>
          {e('슬라이더로 드라이브 조건 조절','Adjust sliders for drive conditions')}
        </span>
      </div>

      {/* ── 슬라이더 컨트롤 ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
        {[
          { lbl: e('차속','Speed'), unit:'km/h', val:vizV,  min:0,    max:200,  step:5,   set:setVizV,  color:'#60a5fa' },
          { lbl: e('구동토크','Drive Torq'), unit:'%', val:vizTq, min:0,    max:100,  step:5,   set:setVizTq, color:'#f59e0b' },
          { lbl: e('횡가속','Lateral g'),  unit:'g', val:vizAy, min:-0.5, max:0.5,  step:0.05,set:setVizAy, color:'#34d399' },
        ].map(s => (
          <div key={s.lbl} style={{ background:`${s.color}08`, borderRadius:7, padding:'8px 10px',
            border:`1px solid ${s.color}30` }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
              <span style={{ fontSize:10, color:COLORS.textDim }}>{s.lbl}</span>
              <span style={{ fontSize:11, fontWeight:800, color:s.color, fontFamily:'monospace' }}>
                {s.val > 0 && s.unit==='g' ? '+':''}{typeof s.val==='number'?s.val.toFixed(s.step<1?2:0):s.val} {s.unit}
              </span>
            </div>
            <input type="range" min={s.min} max={s.max} step={s.step}
              value={s.val} onChange={ev => s.set(parseFloat(ev.target.value))}
              style={{ ...sliderStyle, accentColor:s.color }} />
          </div>
        ))}
      </div>

      {/* ── KPI 배지 ── */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        {kpiBox(e('전륜','Front'), grf.front_pct+'%', '#60a5fa')}
        {kpiBox(e('후륜','Rear'),  grf.rear_pct+'%',  '#f59e0b')}
        {kpiBox(e('좌측','Left'),  grf.left_pct+'%',  '#34d399')}
        {kpiBox(e('우측','Right'), grf.right_pct+'%', '#f87171')}
        {kpiBox(e('요모멘트','Yaw Mz'), (grf.M_z>0?'+':'')+grf.M_z.toFixed(0)+'N·m', '#c084fc')}
        {kpiBox('μ', grf.mu.toFixed(2), '#94a3b8')}
        {kpiBox('TV', grf.tv_on ? '✅ ON' : '— OFF', grf.tv_on ? '#a855f7' : COLORS.textDim)}
      </div>

      {/* ── 메인 레이아웃: SVG 탑뷰 + 스탯 테이블 ── */}
      <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:16, alignItems:'start' }}>

        {/* SVG 차량 탑뷰 */}
        <div style={{ background:COLORS.bg, borderRadius:8, border:`1px solid ${COLORS.border}`, padding:8 }}>
          <svg viewBox="0 0 260 380" width="100%" style={{ display:'block' }}>
            {/* 방향 라벨 */}
            <text x="130" y="16" textAnchor="middle" fill={COLORS.textDim} fontSize="9">▲ {e('전방','Front')}</text>
            <text x="130" y="372" textAnchor="middle" fill={COLORS.textDim} fontSize="9">▽ {e('후방','Rear')}</text>
            <text x="10" y="193" fill={COLORS.textDim} fontSize="8">{e('좌','L')}</text>
            <text x="244" y="193" fill={COLORS.textDim} fontSize="8">{e('우','R')}</text>

            {/* 횡가속 방향 화살표 */}
            {Math.abs(vizAy) > 0.02 && (() => {
              const dir = vizAy < 0 ? -1 : 1;
              const ax1 = 130, ay1 = 190, ax2 = 130 + dir * Math.abs(vizAy) * 70;
              return (
                <>
                  <line x1={ax1} y1={ay1} x2={ax2} y2={ay1} stroke="#34d399" strokeWidth="2" strokeDasharray="4 2"/>
                  <polygon points={`${ax2+dir*5},${ay1} ${ax2-dir*5},${ay1-5} ${ax2-dir*5},${ay1+5}`} fill="#34d399"/>
                  <text x={(ax1+ax2)/2} y={ay1-8} textAnchor="middle" fill="#34d399" fontSize="8">
                    {e('횡가속','ay')} {vizAy>0?'+':''}{vizAy.toFixed(2)}g
                  </text>
                </>
              );
            })()}

            {/* 차체 */}
            <rect x={CX-BW/2} y={CY-BH/2} width={BW} height={BH}
              rx="10" fill={`${COLORS.accent}18`} stroke={COLORS.accent} strokeWidth="1.5"/>
            {/* 앞 유리 */}
            <rect x={CX-BW/2+6} y={CY-BH/2+12} width={BW-12} height={24}
              rx="4" fill={`${COLORS.accent}10`} stroke={`${COLORS.accent}60`} strokeWidth="1"/>
            {/* 차량 중심 십자 */}
            <line x1={CX-8} y1={CY} x2={CX+8} y2={CY} stroke={COLORS.border} strokeWidth="1"/>
            <line x1={CX} y1={CY-8} x2={CX} y2={CY+8} stroke={COLORS.border} strokeWidth="1"/>

            {/* 요 모멘트 호 */}
            {Math.abs(grf.M_z) > 5 && (() => {
              const r   = 32;
              const dir = grf.M_z > 0 ? 1 : -1;
              const sa  = -Math.PI/2;
              const ea  = sa + dir * Math.PI * 0.8;
              const x1  = CX + r * Math.cos(sa);
              const y1  = CY + r * Math.sin(sa);
              const x2  = CX + r * Math.cos(ea);
              const y2  = CY + r * Math.sin(ea);
              const sweep = dir > 0 ? '0,1' : '0,0';
              return (
                <path d={`M ${x1} ${y1} A ${r} ${r} 0 ${sweep} ${x2} ${y2}`}
                  fill="none" stroke="#c084fc" strokeWidth="2" strokeDasharray="5 3"
                  markerEnd="url(#arrow_yaw)"/>
              );
            })()}
            <defs>
              <marker id="arrow_yaw" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#c084fc"/>
              </marker>
            </defs>

            {/* 4륜 렌더링 */}
            {wheels.map(w => {
              const col    = utilColor(w.u);
              const arrowL = tArrow(w.T);
              const fzBarH = fzH(w.Fz);
              const isLeft = w.cx < CX;
              const isFront = w.cy < CY;

              return (
                <g key={w.key}>
                  {/* Fz 수직하중 바 (타이어 외측) */}
                  <rect
                    x={isLeft ? w.cx-TW/2-8 : w.cx+TW/2+2}
                    y={w.cy - fzBarH/2}
                    width={5} height={fzBarH}
                    fill="#60a5fa" opacity="0.7" rx="1"/>

                  {/* 타이어 패치 (마찰원 활용률 색상) */}
                  <rect x={w.cx-TW/2} y={w.cy-TH/2} width={TW} height={TH}
                    rx="4" fill={`${col}55`} stroke={col} strokeWidth="2"/>

                  {/* 구동 토크 화살표 (전방) */}
                  {w.T > 1 && (
                    <>
                      <line
                        x1={w.cx} y1={w.cy-TH/2}
                        x2={w.cx} y2={w.cy-TH/2-arrowL}
                        stroke={col} strokeWidth="2.5"/>
                      <polygon
                        points={`${w.cx},${w.cy-TH/2-arrowL-6} ${w.cx-5},${w.cy-TH/2-arrowL} ${w.cx+5},${w.cy-TH/2-arrowL}`}
                        fill={col}/>
                    </>
                  )}
                  {/* 회생 (음수 토크) */}
                  {w.T < -1 && (
                    <>
                      <line
                        x1={w.cx} y1={w.cy+TH/2}
                        x2={w.cx} y2={w.cy+TH/2+arrowL}
                        stroke="#38bdf8" strokeWidth="2" strokeDasharray="4 2"/>
                      <polygon
                        points={`${w.cx},${w.cy+TH/2+arrowL+5} ${w.cx-4},${w.cy+TH/2+arrowL} ${w.cx+4},${w.cy+TH/2+arrowL}`}
                        fill="#38bdf8"/>
                    </>
                  )}

                  {/* 마찰원 (점선 원) */}
                  <circle cx={w.cx} cy={w.cy} r={TW/2+4}
                    fill="none" stroke={col} strokeWidth="0.8" strokeDasharray="3 3" opacity="0.6"/>

                  {/* 라벨 */}
                  <text x={w.cx} y={w.cy+2} textAnchor="middle" fill={COLORS.text} fontSize="8" fontWeight="700">
                    {w.label}
                  </text>
                  {/* 토크값 */}
                  <text x={w.cx} y={w.cy+12} textAnchor="middle" fill={col} fontSize="7" fontFamily="monospace">
                    {w.T.toFixed(0)}N·m
                  </text>
                  {/* 활용률% */}
                  <text x={w.cx} y={isFront ? w.cy-TH/2-arrowL-12 : w.cy+TH/2+arrowL+14}
                    textAnchor="middle" fill={col} fontSize="7" fontFamily="monospace">
                    {(w.u*100).toFixed(0)}%μ
                  </text>
                </g>
              );
            })}

            {/* 전후 분배 표시 (차체 우측) */}
            {[
              { label:e('전','F'), pct:grf.front_pct, y:CY-offs.y, col:'#60a5fa' },
              { label:e('후','R'), pct:grf.rear_pct,  y:CY+offs.y, col:'#f59e0b' },
            ].map(d => (
              <g key={d.label}>
                <text x={220} y={d.y} textAnchor="middle" fill={d.col} fontSize="8" fontWeight="700">{d.label}</text>
                <text x={220} y={d.y+11} textAnchor="middle" fill={d.col} fontSize="9" fontFamily="monospace">{d.pct}%</text>
              </g>
            ))}
          </svg>

          {/* 범례 */}
          <div style={{ display:'flex', gap:10, justifyContent:'center', marginTop:4, flexWrap:'wrap' }}>
            {[['🟢', e('저활용','Low util')], ['🟡', e('중간','Mid')], ['🔴', e('한계근접','Near limit')],
              ['━', e('구동력','Drive Fx')], ['┄ ','Fz'], ['○', e('마찰원','Friction ○')]].map(([icon, lbl]) => (
              <span key={lbl} style={{ fontSize:8, color:COLORS.textDim }}>{icon} {lbl}</span>
            ))}
          </div>
        </div>

        {/* 우측: 4륜 상세 스탯 */}
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>

          {/* 전후 분배 바 */}
          <div style={{ background:COLORS.bg, borderRadius:7, padding:'10px 12px', border:`1px solid ${COLORS.border}` }}>
            <div style={{ fontSize:10, fontWeight:700, color:COLORS.text, marginBottom:8 }}>
              {e('전후 구동력 분배','Front / Rear Drive Distribution')}
            </div>
            <div style={{ position:'relative', height:20, borderRadius:10, overflow:'hidden',
              background:COLORS.border, marginBottom:4 }}>
              <div style={{ position:'absolute', left:0, top:0, height:'100%',
                width:`${grf.front_pct}%`, background:'#60a5fa', transition:'width 0.3s' }}/>
              <div style={{ position:'absolute', width:'100%', top:0, height:'100%',
                display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 8px' }}>
                <span style={{ fontSize:9, fontWeight:800, color:'#fff' }}>{e('전','F')} {grf.front_pct}%</span>
                <span style={{ fontSize:9, fontWeight:800, color:'#fff' }}>{e('후','R')} {grf.rear_pct}%</span>
              </div>
            </div>
            <div style={{ fontSize:10, fontWeight:700, color:COLORS.text, marginBottom:6, marginTop:8 }}>
              {e('좌우 구동력 분배','Left / Right Drive Distribution')}
            </div>
            <div style={{ position:'relative', height:20, borderRadius:10, overflow:'hidden',
              background:COLORS.border }}>
              <div style={{ position:'absolute', left:0, top:0, height:'100%',
                width:`${grf.left_pct}%`, background:'#34d399', transition:'width 0.3s' }}/>
              <div style={{ position:'absolute', width:'100%', top:0, height:'100%',
                display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 8px' }}>
                <span style={{ fontSize:9, fontWeight:800, color:'#fff' }}>{e('좌','L')} {grf.left_pct}%</span>
                <span style={{ fontSize:9, fontWeight:800, color:'#fff' }}>{e('우','R')} {grf.right_pct}%</span>
              </div>
            </div>
          </div>

          {/* 4륜 상세 테이블 */}
          <div style={{ overflowX:'auto', borderRadius:7, border:`1px solid ${COLORS.border}` }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
              <thead>
                <tr style={{ background:COLORS.panel }}>
                  {[e('휠','Wheel'), 'T (N·m)', 'Fx (N)', 'Fz (N)', e('마찰원%','Util%')].map(h => (
                    <th key={h} style={{ padding:'6px 10px', textAlign:'center', color:COLORS.textDim,
                      fontFamily:'monospace', fontSize:9, borderBottom:`2px solid ${COLORS.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label:e('전좌 FL','FL'), T:grf.T_FL, Fx:grf.Fx_FL, Fz:grf.Fz_FL, u:grf.frUtil.FL },
                  { label:e('전우 FR','FR'), T:grf.T_FR, Fx:grf.Fx_FR, Fz:grf.Fz_FR, u:grf.frUtil.FR },
                  { label:e('후좌 RL','RL'), T:grf.T_RL, Fx:grf.Fx_RL, Fz:grf.Fz_RL, u:grf.frUtil.RL },
                  { label:e('후우 RR','RR'), T:grf.T_RR, Fx:grf.Fx_RR, Fz:grf.Fz_RR, u:grf.frUtil.RR },
                ].map((row, i) => (
                  <tr key={row.label} style={{ borderBottom:`1px solid ${COLORS.border}`,
                    background: i % 2 === 0 ? COLORS.bg : COLORS.cardBg }}>
                    <td style={{ padding:'6px 10px', fontFamily:'monospace', fontWeight:700, color:'#a855f7', textAlign:'center' }}>{row.label}</td>
                    <td style={{ padding:'6px 10px', textAlign:'center', color:COLORS.text, fontFamily:'monospace' }}>{row.T.toFixed(1)}</td>
                    <td style={{ padding:'6px 10px', textAlign:'center', color:COLORS.textDim, fontFamily:'monospace' }}>{row.Fx.toFixed(0)}</td>
                    <td style={{ padding:'6px 10px', textAlign:'center', color:'#60a5fa', fontFamily:'monospace' }}>{row.Fz.toFixed(0)}</td>
                    <td style={{ padding:'6px 10px', textAlign:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'center' }}>
                        <div style={{ width:44, height:6, background:COLORS.border, borderRadius:3 }}>
                          <div style={{ width:`${row.u*100}%`, height:'100%', background:utilColor(row.u), borderRadius:3 }}/>
                        </div>
                        <span style={{ fontFamily:'monospace', fontSize:9, color:utilColor(row.u) }}>
                          {(row.u*100).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 요 모멘트 */}
          {grf.tv_on && (
            <div style={{ background:'rgba(192,132,252,0.08)', border:'1px solid rgba(192,132,252,0.25)',
              borderRadius:7, padding:'8px 12px', fontSize:10 }}>
              <div style={{ fontWeight:700, color:'#c084fc', marginBottom:4 }}>
                🔄 TV Yaw Moment (M_z)
              </div>
              <div style={{ fontFamily:'monospace', fontSize:13, color:'#c084fc', fontWeight:900 }}>
                {grf.M_z > 0 ? '+':''}{grf.M_z.toFixed(0)} N·m
              </div>
              <div style={{ fontSize:9, color:COLORS.textDim, marginTop:3 }}>
                {e(`양(+) = 반시계(좌선회 안정화) · 음(-) = 시계(우선회 안정화)`,
                   `+ = CCW (left-turn stab) · − = CW (right-turn stab)`)}
              </div>
            </div>
          )}

          {/* 접지 조건 배지 */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[
              [e('노면','Road'), params.road_condition || 'dry', '#94a3b8'],
              ['μ', grf.mu.toFixed(2), '#f59e0b'],
              [e('모드','Mode'), grf.mode, '#a855f7'],
              ['AWD', params.awd_enable ? 'ON' : 'OFF', params.awd_enable ? '#4ade80' : COLORS.textDim],
            ].map(([k, v, c]) => (
              <div key={k} style={{ background:`${c}15`, border:`1px solid ${c}40`, borderRadius:5,
                padding:'4px 10px', fontSize:9 }}>
                <span style={{ color:COLORS.textDim }}>{k}: </span>
                <span style={{ color:c, fontWeight:700 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
// ── AFSectionHeader ──
function AFSectionHeader({ catId, sectionId, graphs }) {
  const COLORS = useTheme();
  const lang = useLang();
  const cat = AF_CATS.find(c=>c.id===catId);
  const sec = cat?.sections.find(s=>s.id===sectionId);
  if (!cat || !sec) return null;
  const purposeText = lang==="en" ? (sec.purposeEn || sec.purpose) : sec.purpose;
  return (
    <div style={{ marginBottom:16, padding:"12px 16px", borderRadius:10,
      background:`${cat.color}10`, border:`1px solid ${cat.color}40` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:900, color:cat.color, fontFamily:mono, marginBottom:4 }}>
            {cat.label} — {sec.label}
          </div>
          <div style={{ fontSize:11, color:COLORS.textDim }}>
            {lang==="en" ? "📌 Purpose: " : "📌 목적: "}{purposeText}
          </div>
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {graphs.map(g=>(
            <span key={g} style={{ fontSize:9, fontWeight:700, fontFamily:mono,
              background:`${cat.color}20`, color:cat.color,
              padding:"2px 8px", borderRadius:4, border:`1px solid ${cat.color}40` }}>{g}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
// ── TorqueMap3D ──
function TorqueMap3D({ params, height = 340 }) {
  const COLORS = useTheme();
  const mountRef   = React.useRef(null);
  const rendererRef= React.useRef(null);
  const animRef    = React.useRef(null);
  // [V41-Fix-3D] 'loading' → 폴링 대기, 'ok' → 렌더링, 'no_three' → CDN 실패
  const [status,   setStatus] = React.useState('loading');

  const buildGeometry = React.useCallback((TH) => {
    const NR = 30, NI = 25;
    const rpm_max = 15000, Iq_max = params.I_peak || 400;
    const T_max   = params.T_max_motor || 320;
    const P_max_W = params.P_max_wheel || 240000;
    const lpm     = params.lambda_pm   || 0.12;
    const geo  = new TH.BufferGeometry();
    const pos  = [], col = [];
    const clr  = new TH.Color();
    for (let i = 0; i < NR; i++) {
      for (let j = 0; j < NI; j++) {
        const rpm = (i / (NR-1)) * rpm_max;
        const Iq  = (j / (NI-1)) * Iq_max;
        const w_m = rpm * Math.PI / 30;
        const T_e = Math.min(T_max, 1.5 * lpm * Iq);
        const Pme = T_e * w_m;
        const T_f = Pme > P_max_W ? P_max_W / Math.max(w_m, 1) : T_e;
        const Tn  = T_f / T_max;
        const x   = (i/(NR-1) - 0.5) * 4;
        const z   = (j/(NI-1) - 0.5) * 4;
        const y   = Tn * 2.5;
        pos.push(x, y, z);
        clr.setHSL(0.67 - Tn * 0.67, 1.0, 0.28 + Tn * 0.38);
        col.push(clr.r, clr.g, clr.b);
      }
    }
    const idx = [];
    for (let i = 0; i < NR-1; i++) for (let j = 0; j < NI-1; j++) {
      const a = i*NI+j, b=a+1, c=(i+1)*NI+j, d=c+1;
      idx.push(a,b,d, a,d,c);
    }
    geo.setIndex(idx);
    geo.setAttribute('position', new TH.Float32BufferAttribute(pos,3));
    geo.setAttribute('color',    new TH.Float32BufferAttribute(col,3));
    geo.computeVertexNormals();
    return { geo, NR, NI };
  }, [params]);

  // [V41-Fix-3D] THREE.js CDN 로드 완료 감지 — 폴링(100ms × 최대 100회 = 10초)
  React.useEffect(() => {
    let TH = null;
    try { TH = (typeof THREE !== 'undefined') ? THREE : null; } catch(_) {}
    if (TH) { setStatus('ready'); return; }
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      try { TH = (typeof THREE !== 'undefined') ? THREE : null; } catch(_) {}
      if (TH) { clearInterval(poll); setStatus('ready'); }
      else if (attempts >= 100) { clearInterval(poll); setStatus('no_three'); }
    }, 100);
    return () => clearInterval(poll);
  }, []);

  // [V41-Fix-3D] THREE 로드 완료(status==='ready') 후 WebGL 씬 초기화
  React.useEffect(() => {
    if (status !== 'ready') return;
    const container = mountRef.current;
    if (!container) return;
    let TH;
    try { TH = (typeof THREE !== 'undefined') ? THREE : null; } catch(e) { TH = null; }
    if (!TH) { setStatus('no_three'); return; }
    setStatus('ok');

    const w = container.clientWidth  || 600;
    const h = height;
    const scene    = new TH.Scene();
    scene.background = new TH.Color(0x060d1a);

    const camera = new TH.PerspectiveCamera(45, w/h, 0.1, 100);
    camera.position.set(6, 5, 7);
    camera.lookAt(0, 1, 0);

    const renderer = new TH.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.innerHTML = '';
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    scene.add(new TH.AmbientLight(0xffffff, 0.45));
    const dir = new TH.DirectionalLight(0xffffff, 1.3);
    dir.position.set(5, 10, 5); scene.add(dir);
    const back = new TH.DirectionalLight(0x4488ff, 0.4);
    back.position.set(-5,-3,-5); scene.add(back);

    const { geo } = buildGeometry(TH);
    const mat   = new TH.MeshPhongMaterial({ vertexColors: true, side: TH.DoubleSide, shininess: 80 });
    const mesh  = new TH.Mesh(geo, mat);
    scene.add(mesh);
    const wfMat = new TH.MeshBasicMaterial({ color:0xffffff, wireframe:true, opacity:0.1, transparent:true });
    scene.add(new TH.Mesh(geo, wfMat));

    const grid = new TH.GridHelper(8, 16, 0x1e3a5f, 0x0f2040);
    grid.position.y = -0.1; scene.add(grid);
    scene.add(new TH.AxesHelper(3));

    let theta = 0;
    const R = 10;
    const animate = () => {
      animRef.current = requestAnimationFrame(animate);
      theta += 0.0035;
      camera.position.x = Math.sin(theta)*R;
      camera.position.z = Math.cos(theta)*R;
      camera.lookAt(0, 1, 0);
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const nw = container.clientWidth;
      renderer.setSize(nw, h);
      camera.aspect = nw / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', onResize);
      renderer.dispose(); geo.dispose(); mat.dispose(); wfMat.dispose();
    };
  }, [params, height, buildGeometry, status]);

  if (status === 'loading') return (
    <div style={{ height, display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', background:'#060d1a', borderRadius:8, gap:10 }}>
      <div style={{ fontSize:28 }}>⏳</div>
      <div style={{ fontSize:11, color:'#64748b', textAlign:'center' }}>
        Three.js CDN 로드 중…<br/>
        <code style={{ fontSize:9, color:'#334155' }}>three.min.js r128</code>
      </div>
    </div>
  );

  if (status === 'no_three') return (
    <div style={{ height, display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', background:'#060d1a', borderRadius:8, gap:10 }}>
      <div style={{ fontSize:32 }}>🔌</div>
      <div style={{ fontSize:11, color:'#64748b', textAlign:'center' }}>
        Three.js CDN 미연결<br/>
        <code style={{ fontSize:9, color:'#334155' }}>
          https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js
        </code>
      </div>
    </div>
  );

  return (
    <div style={{ position:'relative' }}>
      <div ref={mountRef} style={{ width:'100%', height, borderRadius:8, overflow:'hidden',
        border:'1px solid #1e293b' }} />
      <div style={{ position:'absolute', top:8, left:10, fontSize:9, color:'#94a3b8',
        background:'rgba(6,13,26,0.85)', padding:'4px 10px', borderRadius:6, backdropFilter:'blur(4px)', lineHeight:1.7 }}>
        <strong>X</strong>: RPM (0–15k) &nbsp;|&nbsp; <strong>Z</strong>: Iq (0–{(params.I_peak||400)|0} A) &nbsp;|&nbsp; <strong>Y</strong>: 토크<br/>
        🔵→🔴 낮은 토크 → 최대 토크
      </div>
      <div style={{ position:'absolute', bottom:8, right:10, fontSize:9, color:'#475569',
        background:'rgba(6,13,26,0.85)', padding:'3px 8px', borderRadius:5 }}>
        T_max={params.T_max_motor||320} Nm &nbsp;|&nbsp; P_max={(((params.P_max_wheel||240000)/1000)|0)} kW
      </div>
    </div>
  );
}

export {
  AWDGroundForceViz,
  AFSectionHeader,
  TorqueMap3D,
};